import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface PenInteractiveProps {
  accent: string;
}
const STEPS = [{
  title: 'Prepare',
  body: 'Check the pen and attach a new needle, as described in the Instructions for Use.',
  highlight: 'needle'
}, {
  title: 'Dial the dose',
  body: 'Turn the dose dial to the prescribed dose.',
  highlight: 'dial'
}, {
  title: 'Inject',
  body: 'Inject subcutaneously, pressing the button as described in the Instructions for Use.',
  highlight: 'button'
}, {
  title: 'Complete',
  body: 'Hold, remove and dispose of the needle safely. Store the pen as instructed.',
  highlight: 'body'
}];
export function PenInteractive({
  accent
}: PenInteractiveProps) {
  const [step, setStep] = useState(0);
  const s = STEPS[step];
  const glow = (part: string) => s.highlight === part ? accent : '#C9D3E0';
  return <div className="grid grid-cols-[1.1fr_1fr] items-center gap-6" data-id="element-211" data-mp="pf7xn0mt802y">
      <div className="rounded-3xl border border-navy-900/[0.08] bg-white px-4 py-8" data-id="element-212" data-mp="2pcjz8qah582j">
        <svg viewBox="0 0 400 90" className="w-full" role="img" aria-label={`Pen schematic, step: ${s.title}`} data-id="element-213" data-mp="2tdvnardp9l8m">
          <motion.rect x="10" y="40" width="40" height="10" rx="3" animate={{
          fill: glow('needle')
        }} data-id="element-214" data-mp="2fecqguqc9wbm" />
          <rect x="50" y="28" width="30" height="34" rx="6" fill="#E1ECEC" data-id="element-215" data-mp="2kco29b072gwh" />
          <motion.rect x="80" y="22" width="220" height="46" rx="14" animate={{
          fill: glow('body')
        }} opacity={0.5} data-id="element-216" data-mp="25ob4c8hjqnzc" />
          <rect x="110" y="34" width="70" height="22" rx="4" fill="#FFFFFF" data-id="element-217" data-mp="2afjej6qpaddk" />
          <motion.rect x="300" y="18" width="50" height="54" rx="10" animate={{
          fill: glow('dial')
        }} data-id="element-218" data-mp="1wnlcffvephqw" />
          <motion.rect x="350" y="30" width="34" height="30" rx="8" animate={{
          fill: glow('button')
        }} data-id="element-219" data-mp="21mplm6ibzfh8" />
        </svg>
        <p className="mt-4 text-center text-[11px] text-slate-400" data-id="element-220" data-mp="1mox81jwch15o">Schematic only — not the actual device design.</p>
      </div>

      <div data-id="element-221" data-mp="1rn73zwpw85sc">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{
        color: accent
      }} data-id="element-222" data-mp="1ro09yvh12r9f">
          Step {step + 1} of {STEPS.length}
        </p>
        <motion.div key={step} initial={{
        opacity: 0,
        x: 10
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-223" data-mp="13snb92zfc7br">
          <p className="mt-2 font-serif text-[24px] text-navy-900" data-id="element-224" data-mp="ytkee5tll7ev">{s.title}</p>
          <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-225" data-mp="tv0su6ix1xn1">{s.body}</p>
        </motion.div>
        <div className="mt-5 flex items-center gap-2" data-id="element-226" data-mp="1rihklakjbdr9">
          <button type="button" onClick={() => setStep(v => Math.max(0, v - 1))} disabled={step === 0} aria-label="Previous step" className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-900/10 text-navy-700 transition hover:bg-mist-100 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-227" data-mp="1rhzqoklchbmi">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-228" data-mp="16bl3oncuu2b" />
          </button>
          <button type="button" onClick={() => setStep(v => Math.min(STEPS.length - 1, v + 1))} disabled={step === STEPS.length - 1} aria-label="Next step" className="flex h-10 w-10 items-center justify-center rounded-full bg-navy-900 text-white transition hover:bg-navy-800 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-229" data-mp="150gxnpzzkao">
            <ChevronRightIcon className="h-4 w-4" data-id="element-230" data-mp="2whxx7u7uvoem" />
          </button>
          <div className="ml-3 flex gap-1.5" data-id="element-231" data-mp="2wjquiiwkww9y">
            {STEPS.map((_, i) => <span key={i} className="h-1.5 rounded-full transition-all" style={{
            width: i === step ? 20 : 6,
            backgroundColor: i === step ? accent : '#C9D3E0'
          }} data-id="element-232" data-mp="30hg7lw21wh37" data-mpi={i} />)}
          </div>
        </div>
      </div>
    </div>;
}